import { CalloutIcon, getCalloutVariantIcon } from "@/tiptap/icons/callout-icon"
import { CALLOUT_VARIANTS } from "@/tiptap/nodes/callout-node/callout-node-extension"
import {
  FOCUS_NO_SCROLL,
  isNodeInSchema,
  isNodeTypeSelected,
  runWithPreservedWindowScroll,
  selectionWithinConvertibleTypes,
} from "@/tiptap/lib/tiptap-utils"

const LIST_NODE_TYPES = new Set(["bulletList", "orderedList", "taskList"])

function getTopLevelSelectedListNodes(state) {
  const { doc, selection } = state
  const selectedLists = []
  const seen = new Set()

  const addList = (node, pos) => {
    if (!LIST_NODE_TYPES.has(node.type.name) || seen.has(pos)) return

    seen.add(pos)
    selectedLists.push({ node, pos })
  }

  selection.ranges.forEach(({ $from, $to }) => {
    for (let depth = $from.depth; depth > 0; depth--) {
      addList($from.node(depth), $from.before(depth))
    }

    for (let depth = $to.depth; depth > 0; depth--) {
      addList($to.node(depth), $to.before(depth))
    }

    doc.nodesBetween($from.pos, $to.pos, addList)
  })

  return selectedLists.filter(({ node, pos }) => {
    return !selectedLists.some(({ node: otherNode, pos: otherPos }) => {
      return otherPos < pos && pos < otherPos + otherNode.nodeSize
    })
  })
}

function wrapSelectedListInCallout(editor, variant) {
  if (!editor?.state?.doc || !editor.state.selection) return false

  const selectedLists = getTopLevelSelectedListNodes(editor.state)
  if (selectedLists.length !== 1) return false

  const [{ node, pos }] = selectedLists
  const { from, to } = editor.state.selection
  if (from < pos || to > pos + node.nodeSize) return false

  return editor
    .chain()
    .focus()
    .setNodeSelection(pos)
    .wrapIn("callout", { variant })
    .run()
}

export function getCalloutVariantLabel(variant) {
  switch (variant) {
    case "info":
      return "Info"
    case "success":
      return "Success"
    case "warning":
      return "Warning"
    case "danger":
      return "Danger"
    default:
      return variant
  }
}

export function selectCalloutVariant(editor, variant) {
  if (!editor) {
    return false
  }

  return (
    wrapSelectedListInCallout(editor, variant) ||
    runWithPreservedWindowScroll(() =>
      editor.chain().focus(undefined, FOCUS_NO_SCROLL).setCallout({ variant }).run())
  )
}

export function canToggleCallout(editor, turnInto = true) {
  if (!editor || !editor.isEditable) return false
  if (
    !isNodeInSchema("callout", editor) ||
    isNodeTypeSelected(editor, ["image"])
  )
    return false

  if (!turnInto) {
    return editor.can().setCallout({ variant: "info" })
  }

  if (
    !selectionWithinConvertibleTypes(editor, [
      "paragraph",
      "heading",
      "bulletList",
      "orderedList",
      "taskList",
      "blockquote",
      "callout",
      "codeBlock",
    ])
  )
    return false

  return editor.can().setCallout({ variant: "info" }) || editor.can().clearNodes()
}

export function buildCalloutVariantActions(editor) {
  const canToggle = canToggleCallout(editor)

  return CALLOUT_VARIANTS.map((variant) => ({
    variant,
    calloutVariant: variant,
    label: getCalloutVariantLabel(variant),
    icon: getCalloutVariantIcon(variant),
    disabled: !canToggle,
    isActive: Boolean(editor?.isActive("callout", { variant })),
    onClick: () => selectCalloutVariant(editor, variant),
    onSelect: () => selectCalloutVariant(editor, variant),
  }))
}

export function buildCalloutAction(editor) {
  return {
    icon: CalloutIcon,
    label: "Callout",
    disabled: !canToggleCallout(editor),
    isActive: Boolean(editor?.isActive("callout")),
    items: buildCalloutVariantActions(editor),
  }
}
